import * as React from "react";
import type { Evidence } from "@/types/evidences";
import { Loader2 } from "lucide-react";
import { MarkdownRenderer } from "@/components/feature/markdown/markdownRenderer";
import { reformatCitations } from "@/modules/chat/chat.utils";
import { useArtefactExtraction } from "@/hooks/useArtefactExtraction";
import { ProcessingPlaceholder } from "./components/processingPlaceholder";
import { EditMode } from "./components/editMode";
import { TypewriterContent } from "./components/typewriterContent";
import { EventDateAlert } from "@/modules/snapshots/outcome-details/components/EventDateAlert";
import { TrafficLightRating } from "@/modules/snapshots/businessReports.types";
import type { PlacedSnapshotCitation, ResultDetails } from "@/modules/snapshots/businessReports.types";
import { ResultDetailsSection } from "@/modules/snapshots/resultDetails/ResultDetailsSection";
import type { SearchSourcesData } from "@/types/open-chat";
import { SearchResultsBox } from "@/modules/open-chat/components/SearchResultsBox";

interface Props {
  content: string;
  relevancy?: boolean | null;
  evidences: Evidence[];
  snapshotCitations: PlacedSnapshotCitation[];
  resultDetails?: ResultDetails | null;
  webSearchSources: SearchSourcesData;
  processing: boolean;
  isEdit: boolean;
  isProcessing: boolean;
  date: string | null;
  isDate: boolean;
  errorMessage: string | null;
  editedContent: string;
  editedRating: TrafficLightRating | null;
  isLoading: boolean;
  typedContent: string;
  progressPercentage: number;
  isTrafficLight: boolean;
  onContentChange: (content: string) => void;
  onRatingChange: (rating: TrafficLightRating | null) => void;
  onSave: () => void;
  onCancel: () => void;
}

export const Content: React.FC<Props> = ({
  content,
  relevancy = null,
  evidences,
  snapshotCitations,
  resultDetails,
  webSearchSources,
  processing,
  isEdit,
  isProcessing,
  date,
  isDate,
  errorMessage,
  editedContent,
  editedRating,
  isLoading,
  typedContent,
  progressPercentage,
  isTrafficLight,
  onContentChange,
  onRatingChange,
  onSave,
  onCancel,
}) => {
  const [processedContent, inlineArtefacts] = useArtefactExtraction(content);
  const hasRelevancy = relevancy != null && !errorMessage;

  if (isProcessing) {
    return <ProcessingPlaceholder />;
  }

  if (isEdit) {
    return (
      <EditMode
        editedContent={editedContent}
        editedRating={editedRating}
        isLoading={isLoading}
        showRating={isTrafficLight}
        onContentChange={onContentChange}
        onRatingChange={onRatingChange}
        onSave={onSave}
        onCancel={onCancel}
      />
    );
  }

  if (processing && !hasRelevancy) {
    return (
      <div className="text-sm leading-relaxed">
        <TypewriterContent typedContent={typedContent} progressPercentage={progressPercentage} />
      </div>
    );
  }

  return (
    <div className="space-y-2 text-sm leading-relaxed">
      {hasRelevancy && <p>{relevancy ? "True" : "False"}</p>}
      {webSearchSources.sources.length > 0 && <SearchResultsBox searchSources={webSearchSources} isSearching={false} />}
      {content && (
        <MarkdownRenderer
          content={reformatCitations(processedContent)}
          evidences={evidences}
          processCitations={true}
          inlineArtefacts={inlineArtefacts}
          snapshotCitations={snapshotCitations}
          webSearchSources={webSearchSources.sources}
        />
      )}
      {date && <EventDateAlert date={date} />}
      <ResultDetailsSection details={resultDetails} />
      {!content &&
        !date &&
        !hasRelevancy &&
        (errorMessage ? (
          <span className="flex items-center gap-1 text-sm text-red-600" title={errorMessage}>
            Failed
          </span>
        ) : isDate ? (
          <span className="text-muted-foreground">N/A</span>
        ) : (
          <span className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Generating…
          </span>
        ))}
    </div>
  );
};
